"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useInvertableSelect = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2022 GoodData Corporation
var react_1 = require("react");
var differenceBy_1 = tslib_1.__importDefault(require("lodash/differenceBy"));
var intersectionBy_1 = tslib_1.__importDefault(require("lodash/intersectionBy"));
var keyBy_1 = tslib_1.__importDefault(require("lodash/keyBy"));
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
/**
 * @internal
 */
function useInvertableSelect(props) {
    var items = props.items, totalItemsCount = props.totalItemsCount, getItemKey = props.getItemKey, searchString = props.searchString, _a = props.isInverted, isInverted = _a === void 0 ? true : _a, selectedItems = props.selectedItems, _b = props.onSelect, onSelect = _b === void 0 ? noop_1.default : _b;
    var isSearch = searchString.length > 0;
    var isSelectionEmpty = selectedItems.length === 0;
    var selectionMap = react_1.useMemo(function () {
        return keyBy_1.default(selectedItems, getItemKey);
    }, [selectedItems, getItemKey]);
    var itemsNotInSelection = react_1.useMemo(function () { return differenceBy_1.default(items, selectedItems, getItemKey); }, [items, selectedItems, getItemKey]);
    var itemsInSelection = react_1.useMemo(function () { return intersectionBy_1.default(items, selectedItems, getItemKey); }, [items, selectedItems, getItemKey]);
    var loadedSelectedItems = react_1.useMemo(
    // For inverted list, items are selected when they are not in the selection
    function () { return (isInverted ? itemsNotInSelection : itemsInSelection); }, [isInverted, itemsNotInSelection, itemsInSelection]);
    var loadedUnselectedItems = react_1.useMemo(
    // For inverted list, items are unselected when they are in the selection
    function () { return (isInverted ? itemsInSelection : itemsNotInSelection); }, [isInverted, itemsNotInSelection, itemsInSelection]);
    var selectionState = react_1.useMemo(function () {
        if (!isSearch) {
            // Negative filter with no selection
            if (isInverted && isSelectionEmpty) {
                return "all";
            }
            // Positive filter with all items selected
            if (!isInverted && loadedSelectedItems.length === totalItemsCount) {
                return "all";
            }
            // Positive filter with no selection
            if (!isInverted && isSelectionEmpty) {
                return "none";
            }
            // Negative filter with all items selected
            if (isInverted && loadedUnselectedItems.length === totalItemsCount) {
                return "none";
            }
        }
        else {
            if (isInverted && itemsInSelection.length === 0) {
                return "all";
            }
            if (!isInverted && itemsInSelection.length === totalItemsCount) {
                return "all";
            }
            if (isInverted && itemsInSelection.length === totalItemsCount) {
                return "none";
            }
            if (!isInverted && itemsInSelection.length === 0) {
                return "none";
            }
        }
        return "partial";
    }, [isInverted, isSelectionEmpty, totalItemsCount, loadedSelectedItems, isSearch, loadedUnselectedItems]);
    var getIsItemSelected = react_1.useCallback(function (item) {
        var itemKey = getItemKey(item);
        return isInverted ? !selectionMap[itemKey] : !!selectionMap[itemKey];
    }, [isInverted, selectionMap, getItemKey]);
    var selectAll = react_1.useCallback(function () {
        onSelect([], true);
    }, [onSelect]);
    var selectNone = react_1.useCallback(function () {
        onSelect([], false);
    }, [onSelect]);
    var selectOnly = react_1.useCallback(function (item) {
        onSelect([item], false);
    }, [onSelect]);
    var selectItems = react_1.useCallback(function (items) {
        var newItems = isInverted
            ? differenceBy_1.default(selectedItems, items, getItemKey)
            : selectedItems.concat(items);
        if (!isSearch && !isInverted && newItems.length === totalItemsCount) {
            // Selecting last item -> select all
            selectAll();
        }
        else {
            onSelect(newItems, isInverted);
        }
    }, [isInverted, selectedItems, onSelect, selectAll, getItemKey, totalItemsCount, isSearch]);
    var deselectItems = react_1.useCallback(function (items) {
        var newItems = isInverted
            ? selectedItems.concat(items)
            : differenceBy_1.default(selectedItems, items, getItemKey);
        if (!isSearch && isInverted && newItems.length === totalItemsCount) {
            selectNone();
        }
        else {
            onSelect(newItems, isInverted);
        }
    }, [isInverted, selectedItems, onSelect, selectNone, getItemKey, totalItemsCount, isSearch]);
    var onSelectAllCheckboxToggle = react_1.useCallback(function () {
        if (isSearch) {
            if (selectionState === "all" || loadedSelectedItems.length === items.length) {
                // Reduce selection, if is all, or all loaded is selected
                deselectItems(loadedSelectedItems);
            }
            else {
                // Extend selection if is none or partial
                selectItems(loadedUnselectedItems);
            }
        }
        else if (selectionState === "all") {
            // Is all -> select none
            selectNone();
        }
        else {
            // Is partial or none -> select all
            selectAll();
        }
    }, [
        items,
        selectionState,
        loadedUnselectedItems,
        loadedSelectedItems,
        selectItems,
        deselectItems,
        selectAll,
        selectNone,
        isSearch,
    ]);
    var onSelectAllCheckboxChange = react_1.useCallback(function (value) {
        if (isSearch) {
            if (value) {
                selectItems(loadedUnselectedItems);
            }
            else {
                deselectItems(loadedSelectedItems);
            }
        }
        else if (value) {
            selectAll();
        }
        else {
            selectNone();
        }
    }, [
        items,
        selectionState,
        loadedUnselectedItems,
        loadedSelectedItems,
        selectItems,
        deselectItems,
        selectAll,
        selectNone,
        isSearch,
    ]);
    return {
        onSelectAllCheckboxToggle: onSelectAllCheckboxToggle,
        onSelectAllCheckboxChange: onSelectAllCheckboxChange,
        getIsItemSelected: getIsItemSelected,
        selectionState: selectionState,
        selectItems: selectItems,
        deselectItems: deselectItems,
        selectOnly: selectOnly,
    };
}
exports.useInvertableSelect = useInvertableSelect;
//# sourceMappingURL=useInvertableSelect.js.map